<template>
    <base-dialog :loading="loading" :value="dialog" :width="480" :title="title" @input="val => { if (!val) hide() }" @close="hide">
        <div class="dlg-fields">
            <kimsa-text-field :label="$t('common.name')" :placeholder="$t('routines.give_routine_name_placeholder')"
                dense force-value :value="name" @change="name = $event" />
            <kimsa-combobox :label="$t('common.category')" :placeholder="$t('exercises.pick_one_category')" dense
                :items="categories" @change="category = $event" :value="category" item-text="label" ref="categories" />
            <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                :label="$t('exercises.tags')" force-value />
            <kimsa-upload-file :label="$t('common.image')" relative-path="images/routines" mime-types="image/*"
                @change="file = $event" :preview-img="previewImg" ref="img" height="150" @delete="onDeleteImg" />
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="editMode || createMode ? $t('common.next') : $t('common.save')"
                :disabled="disabled" :loading="loading" @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { clearBlocksAssignedRoutine, getPayloadBaseRoutine } from "@/utils/Exercises";
import KimsaTagsPicker from '../../common/inputs/KimsaTagsPicker.vue';
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";

export default {
    name: "SaveAssignedRoutineDialog",
    components: { KimsaCombobox, KimsaUploadFile, KimsaTextField, KimsaTagsPicker, BaseDialog, DialogActions },
    props: {
        collection: {
            type: String,
            default: 'assignedRoutines',
        },
        coachId: {
            type: String,
            default: null
        },
        editMode: {
            type: Boolean,
            default: false
        },
        createMode: {
            type: Boolean,
            default: false
        },
    },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            assignedRoutine: null,
            name: '',
            category: null,
            file: null,
            tags: [],
            currentImg: null,
            currentImgSavePath: null,
            deletedImage: false,
            routineId: null,
        }
    },
    computed: {
        ...mapState(['categoriesOptions', "loadingCategories"]),
        disabled(vm = this) {
            return !vm.name || !vm.category;
        },
        previewImg(vm = this) {
            if (vm.file?.img) return vm.file?.img
            if (vm.deletedImage) return null
            return vm.currentImg
        },
        categories(vm = this) {
            return [...vm.categoriesOptions, { id: 1, key: 'mix', name: 'Mix', value: 'Mix', label: vm.$t('categories.mix') }]
        },
        title(vm = this) {
            if (vm.createMode) return vm.$t('routines.create_first_routine')
            return vm.editMode ? vm.$t('routines.edit_routine') : vm.$t('routines.save_in')
        }
    },
    async mounted() {
        const vm = this
        // console.log(vm)
        if (!vm.categoriesOptions || vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        show(assignedRoutine) {
            const vm = this
            if (!assignedRoutine && !vm.createMode) return vm.toast(vm.$t('routines.error_save_routine'), 'error')
            vm.assignedRoutine = assignedRoutine
            if (vm.editMode) vm.setDefaults()
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.assignedRoutine = null
            vm.name = ''
            if (vm.$refs.categories) vm.$refs.categories.model = null
            if (vm.$refs.img) vm.$refs.img.reset()
            vm.currentImg = null
            vm.currentImgSavePath = null
            vm.deletedImage = false
            vm.category = null
            vm.file = null
            vm.tags = []
        },
        setDefaults() {
            const vm = this;
            vm.routineId = vm.assignedRoutine.objectID
            vm.name = vm.assignedRoutine.name
            vm.category = vm.categories.find(category => category.key === vm.routineObjectiveKey(vm.assignedRoutine.description?.objective)) || null
            vm.tags = vm.assignedRoutine.tags || []
            vm.currentImg = vm.assignedRoutine.img || null
            vm.currentImgSavePath = vm.assignedRoutine.imgSavePath || null
        },
        async save() {
            const vm = this
            const { assignedRoutine, name, category, tags, file, currentImg, currentImgSavePath } = vm
            await vm.withLoading(async () => {

            if (vm.editMode || vm.createMode) {
                let img = { tempUrl: currentImg, savePath: currentImgSavePath }
                if (file) img = await vm.$refs.img.upload()
                let payload = {
                    name: name,
                    'description.objective': category?.name || '',
                    'description.objectiveKey': vm.routineObjectiveKey(category?.name) || '',
                    tags: tags,
                    img: img?.tempUrl || '',
                    imgSavePath: img?.savePath || '',
                }

                if (vm.editMode) {
                    await db.collection('routines').doc(vm.routineId).update(payload)
                } else {
                    let routineRef = await db.collection('routines').add({
                        ...payload,
                        blocks: [{ exercises: [] }],
                        createdAt: Timestamp.now(),
                        coachId: vm.coachId,
                    })
                    vm.routineId = routineRef.id
                }

                vm.hide()
                vm.toast(vm.$t('routines.saved'))
                return vm.pushRoute({ name: `${vm.accountType}.routines.edit`, params: { uid: vm.routineId } })
            } else {
                let blocks = clearBlocksAssignedRoutine(assignedRoutine.blocks)
                let img = null

                if (file) img = await vm.$refs.img.upload()
                let payloadRoutine = getPayloadBaseRoutine(name, assignedRoutine, blocks, category?.name || '', img, tags)

                await db.collection('routines').add(payloadRoutine)
            }

            vm.hide()
            vm.toast(vm.$t('routines.saved'))
            })
        },
        onDeleteImg() {
            const vm = this
            vm.file = null
            vm.deletedImage = true
            vm.$refs.img.reset()
        },
    },

}
</script>

<style scoped>
.dlg-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
</style>